import { type ReactNode, useMemo } from 'react';

import { Checkbox } from '@/components/core/Checkbox';
import { Combobox } from '@/components/core/Combobox/Combobox';
import { DateInput } from '@/components/core/DateInput';
import { Input } from '@/components/core/Input';
import { RadioGroup, RadioGroupItem } from '@/components/core/RadioGroup';
import { LOCAL_STORAGE } from '@/constants';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import type { JobSchedule, PlanAction, PlanOptions } from '../types';

// Angular used $locale.DATETIME_FORMATS.SHORTDAY (no DATE.*_SHORT translation keys exist).
const getShortWeekdays = (): Array<{ key: number; label: string }> => {
    const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY);
    const locale = stored ? stored.replace(/_/g, '-') : undefined;
    return Array.from({ length: 7 }, (_, sundayFirstIndex) => {
        const date = new Date(Date.UTC(2024, 0, 7 + sundayFirstIndex));
        return {
            key: sundayFirstIndex === 0 ? 7 : sundayFirstIndex,
            label: new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(date),
        };
    }).sort((a, b) => a.key - b.key);
};

const REPEAT_TYPES = [
    { key: 'HOUR', labelKey: 'DATE.HOUR' },
    { key: 'DAY', labelKey: 'DATE.DAY' },
    { key: 'WEEK', labelKey: 'DATE.WEEK' },
    { key: 'MONTH', labelKey: 'DATE.MONTH' },
] as const;

const FIELD_LABEL_CLASS = 'tw-flex-none tw-w-2/5 tw-min-w-0 tw-text-right tw-text-t-sm tw-m-0 tw-text-t-secondary tw-truncate';
const PANEL_BUTTON_CLASS = [
    'tw-h-9 tw-w-20 tw-rounded-sm tw-border-solid tw-border tw-border-primary',
    'tw-text-t-sm tw-text-t-primary tw-bg-fill-primary',
    'hover:tw-border-primary-hover active:tw-border-primary-press',
    'focus-visible:tw-border-2 focus-visible:!tw-border-primary-focus',
].join(' ');
const PANEL_BUTTON_SELECTED_CLASS = 'tw-bg-fill-primary tw-border-primary-active';

const toDateValue = (value?: Date | string | number | null): Date | undefined => {
    if (value === null || value === undefined) {
        return undefined;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

const EditorField = ({
    htmlFor,
    label,
    children,
    align = 'center',
}: {
    htmlFor?: string;
    label: string;
    children: ReactNode;
    align?: 'center' | 'start';
}) => (
    <div className={cn('tw-flex tw-flex-row tw-gap-3', align === 'start' ? 'tw-items-start' : 'tw-items-center')}>
        <label
            htmlFor={htmlFor}
            className={cn(FIELD_LABEL_CLASS, align === 'start' && 'tw-h-9 tw-flex tw-items-center tw-justify-end')}
            title={label}
        >
            {label}
        </label>
        <div className="tw-flex-1 tw-min-w-0">{children}</div>
    </div>
);

const toggleNumberInList = (list: number[] | undefined, option: number): number[] => {
    const current = list ?? [];
    const index = current.indexOf(option);
    if (index > -1) {
        return current.filter((entry) => entry !== option);
    }
    return [...current, option];
};

type SchedulePlanProps = {
    planOptions: PlanOptions;
    onChange: (next: PlanOptions) => void;
};

const SchedulePlan = ({ planOptions, onChange }: SchedulePlanProps) => {
    const { t } = useTranslation();
    const daysOfWeek = useMemo(() => getShortWeekdays(), []);
    const schedule: JobSchedule = planOptions.job.schedule ?? {};
    const isScheduleSelected = planOptions.action === 'SCHEDULE_PLAN';
    const isRepeatEnabled = isScheduleSelected && Boolean(planOptions.actionRepeat);
    const repeatTypeOptions = useMemo(
        () => REPEAT_TYPES.map((type) => ({ value: type.key, label: t(type.labelKey) })),
        [t],
    );
    const selectedRepeatType = repeatTypeOptions.find((type) => type.value === schedule.repeatType) ?? null;

    const updateSchedule = (patch: Partial<JobSchedule>) => {
        onChange({
            ...planOptions,
            job: {
                ...planOptions.job,
                schedule: {
                    ...schedule,
                    ...patch,
                },
            },
        });
    };

    const handleActionChange = (action: PlanAction) => {
        const next: PlanOptions = {
            ...planOptions,
            action,
        };
        if (action === 'SCHEDULE_PLAN') {
            next.job = {
                ...planOptions.job,
                schedule: {
                    ...schedule,
                    startDateTime: new Date(),
                },
            };
        }
        onChange(next);
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-5" data-testid="schedule-plan">
            <RadioGroup
                value={planOptions.action}
                onValueChange={(value) => handleActionChange(value as PlanAction)}
                className="tw-gap-4"
            >
                <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-t-sm lg:tw-text-t-base tw-text-t-primary" htmlFor="job_schedule_immediately">
                    <RadioGroupItem id="job_schedule_immediately" value="IMMEDIATELY" />
                    <span>{t('ITEM_LIBRARY_PUBLISH.SCHEDULE.IMMEDIATELY')}</span>
                </label>
                <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-t-sm lg:tw-text-t-base tw-text-t-primary" htmlFor="job_schedule_plan">
                    <RadioGroupItem id="job_schedule_plan" value="SCHEDULE_PLAN" />
                    <span>{t('ITEM_LIBRARY_PUBLISH.SCHEDULE.PLAN')}</span>
                </label>
            </RadioGroup>

            <fieldset
                disabled={!isScheduleSelected}
                className={cn('tw-flex tw-flex-col tw-gap-4 tw-m-0 tw-p-0 tw-border-0', !isScheduleSelected && 'tw-opacity-60')}
            >
                <EditorField htmlFor="job-name" label={t('ITEM_LIBRARY_PUBLISH.attribute.name')}>
                    <Input
                        id="job-name"
                        value={planOptions.job.name ?? ''}
                        onChange={(event) => onChange({
                            ...planOptions,
                            job: { ...planOptions.job, name: event.target.value },
                        })}
                    />
                </EditorField>
                <EditorField htmlFor="job-start" label={t('ITEM_LIBRARY_PUBLISH.attribute.startDateTime')}>
                    <DateInput
                        id="job-start"
                        withTime={true}
                        disabled={!isScheduleSelected}
                        selectedDate={toDateValue(schedule.startDateTime)}
                        onChange={(value) => updateSchedule({
                            startDateTime: value ?? undefined,
                        })}
                        className="tw-bg-white"
                    />
                </EditorField>
            </fieldset>

            <label
                className={cn(
                    'tw-m-0 tw-min-w-0 tw-flex tw-items-center tw-gap-2 tw-pl-6 tw-text-t-sm lg:tw-text-t-base tw-text-t-primary',
                    !isScheduleSelected ? 'tw-opacity-60 tw-cursor-not-allowed' : 'tw-cursor-pointer',
                )}
                htmlFor="job_schedule_repeat"
            >
                <Checkbox
                    id="job_schedule_repeat"
                    isChecked={Boolean(planOptions.actionRepeat)}
                    disabled={!isScheduleSelected}
                    onCheckedChange={(checked) => onChange({
                        ...planOptions,
                        actionRepeat: checked === true,
                    })}
                />
                <span>{t('ITEM_LIBRARY_PUBLISH.SCHEDULE.REPEAT')}</span>
            </label>

            <fieldset
                disabled={!isRepeatEnabled}
                className={cn('tw-flex tw-flex-col tw-gap-4 tw-m-0 tw-p-0 tw-border-0', !isRepeatEnabled && 'tw-opacity-60')}
            >
                <EditorField htmlFor="repeat-every" label={t('ITEM_LIBRARY_PUBLISH.attribute.repeatEvery')}>
                    <Input
                        id="repeat-every"
                        type="number"
                        min={1}
                        max={31}
                        value={schedule.repeatEvery ?? ''}
                        onChange={(event) => updateSchedule({
                            repeatEvery: event.target.value === '' ? undefined : Number(event.target.value),
                        })}
                    />
                </EditorField>
                <EditorField htmlFor="repeat-type" label={t('ITEM_LIBRARY_PUBLISH.attribute.repeatType')}>
                    <div className="tw-w-40" data-testid="repeat-type-dropdown">
                        <Combobox
                            inputId="repeat-type"
                            isSearchable={false}
                            isClearable={false}
                            isDisabled={!isRepeatEnabled}
                            options={repeatTypeOptions}
                            value={selectedRepeatType}
                            placeholder=""
                            aria-label={t('ITEM_LIBRARY_PUBLISH.attribute.repeatType')}
                            onChange={(option: { value: string } | null) => {
                                if (option?.value) {
                                    updateSchedule({ repeatType: option.value });
                                }
                            }}
                        />
                    </div>
                </EditorField>

                {schedule.repeatType === 'WEEK' && (
                    <EditorField align="center" label={t('ITEM_LIBRARY_PUBLISH.attribute.repeatDaysOfWeek')}>
                        <div className="tw-flex tw-flex-wrap tw-gap-3">
                            {daysOfWeek.map((day) => (
                                <label key={day.key} className="tw-m-0 tw-min-w-0 tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-t-sm tw-text-t-secondary">
                                    <Checkbox
                                        isChecked={(schedule.repeatDaysOfWeek ?? []).includes(day.key)}
                                        onCheckedChange={() => updateSchedule({
                                            repeatDaysOfWeek: toggleNumberInList(schedule.repeatDaysOfWeek, day.key),
                                        })}
                                    />
                                    <span>{day.label}</span>
                                </label>
                            ))}
                        </div>
                    </EditorField>
                )}

                {schedule.repeatType === 'MONTH' && (
                    <EditorField align="start" label={t('ITEM_LIBRARY_PUBLISH.attribute.repeatDaysOfMonth')}>
                        <div className="tw-flex tw-flex-wrap tw-gap-2">
                            {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => {
                                const isSelected = (schedule.repeatDaysOfMonth ?? []).includes(day);
                                return (
                                    <button
                                        key={day}
                                        type="button"
                                        id={`day_${day}_button`}
                                        className={cn(
                                            PANEL_BUTTON_CLASS,
                                            isSelected && PANEL_BUTTON_SELECTED_CLASS,
                                        )}
                                        onClick={() => updateSchedule({
                                            repeatDaysOfMonth: toggleNumberInList(schedule.repeatDaysOfMonth, day),
                                        })}
                                    >
                                        {day}
                                    </button>
                                );
                            })}
                        </div>
                    </EditorField>
                )}

                <EditorField htmlFor="job-end" label={t('ITEM_LIBRARY_PUBLISH.attribute.endDateTime')}>
                    <DateInput
                        id="job-end"
                        withTime={true}
                        disabled={!isRepeatEnabled}
                        selectedDate={toDateValue(schedule.endDateTime)}
                        onChange={(value) => updateSchedule({
                            endDateTime: value ?? undefined,
                        })}
                        className="tw-bg-white"
                    />
                </EditorField>
            </fieldset>
        </div>
    );
};

SchedulePlan.displayName = 'SchedulePlan';

export { SchedulePlan, toDateValue, toggleNumberInList };
export type { SchedulePlanProps };
